import React, { useCallback } from 'react';

import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import PageHighlight from 'public/assets/images/highlights/small-pagination-highlight.png';

import { PaginationButton } from '@/components/atoms/Pagination/components/Button';
import { getPaginationItems } from '@/components/atoms/Pagination/functions';
import { LOCALES } from '@/utils/constants';

type PaginationProps = {
  totalNoOfPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
};

const MAX_ITEMS_TO_SHOW = 7;

export const Pagination = ({ totalNoOfPages, currentPage, onPageChange }: PaginationProps) => {
  const { t } = useTranslation(LOCALES.COMMON);

  const paginationAdditionalPages = useBreakpointValue({
    base: 1,
    md: 2,
  });
  const paginationContent = useCallback(
    (totalNoOfPages: number, currentPage: number, paginationAdditionalPages: number) =>
      getPaginationItems(totalNoOfPages, currentPage, paginationAdditionalPages, MAX_ITEMS_TO_SHOW),
    [],
  );
  const isFirst = currentPage === 1;
  const isLast = currentPage === totalNoOfPages;

  const changePage = (page: number) => {
    onPageChange(page);
  };

  return (
    <Flex alignItems="center">
      <PaginationButton onClick={() => changePage(currentPage - 1)} active={!isFirst} text={t('prevPage')} />
      <Flex
        gap={{
          base: 3,
          sm: 4,
        }}
        mx={{
          base: 4,
          xs: 5,
        }}
      >
        {paginationContent(totalNoOfPages, currentPage, paginationAdditionalPages || 1).map((item, index) => {
          const isNumber = !isNaN(+item);
          const isSelected = isNumber && +item === currentPage;
          return (
            <Box
              key={`pagination-item-${index}`}
              p={{
                base: 0,
                xs: 1,
              }}
              position="relative"
              onClick={() => isNumber && changePage(+item)}
              cursor={isNumber ? 'pointer' : 'initial'}
              color={isSelected ? 'violet.500' : 'purple.400'}
            >
              <Text>{item}</Text>
              <AnimatePresence>
                {isSelected && (
                  <Box
                    position="absolute"
                    left={{
                      base: '-9px',
                      xs: '-5px',
                    }}
                    top={{
                      base: 0,
                      xs: '4px',
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      key={item}
                    >
                      <Box as={Image} src={PageHighlight} alt="" minW="25px" minH="22px" zIndex={-1} />
                    </motion.div>
                  </Box>
                )}
              </AnimatePresence>
            </Box>
          );
        })}
      </Flex>
      <PaginationButton onClick={() => changePage(currentPage + 1)} active={!isLast} text={t('nextPage')} />
    </Flex>
  );
};
